import React from 'react';
import { Activity, Flame, Target, Calendar, Dumbbell, BarChart2, ArrowRight, Calculator, Percent } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета TDEE
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Получение названия цели
  const getGoalName = (goalId) => {
    switch (goalId) {
      case 'lose_fast': return 'Интенсивное похудение';
      case 'lose': return 'Плавное похудение';
      case 'maintain': return 'Поддержание веса';
      case 'gain': return 'Набор веса/мышечной массы';
      case 'gain_fast': return 'Активный набор массы';
      default: return 'Поддержание веса';
    }
  };

  // Получение названия формулы
  const getFormulaName = (formulaId) => {
    switch (formulaId) {
      case 'mifflin': return 'Миффлина-Сан Жеора';
      case 'harris': return 'Харриса-Бенедикта';
      case 'katch': return 'Катч-МакАрдла';
      default: return 'Миффлина-Сан Жеора';
    }
  };

  // Получение названия уровня активности
  const getActivityName = (activityId) => {
    switch (activityId) {
      case 'sedentary': return 'Сидячий образ жизни';
      case 'light': return 'Легкая активность';
      case 'moderate': return 'Умеренная активность';
      case 'high': return 'Высокая активность';
      case 'very_high': return 'Очень высокая активность';
      default: return 'Умеренная активность';
    }
  };

  return (
    <ToolResult title="Результаты расчета TDEE">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
        {/* BMR */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Flame className="w-4 h-4"/>
            Базовый метаболизм (BMR)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatNumber(results.bmr, 0)} ккал
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            Минимальное количество калорий для поддержания основных функций организма
          </p>
        </div>

        {/* TDEE */}
        <div className="rounded-lg bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/30 p-5 border border-emerald-100 dark:border-emerald-800/50 shadow-sm">
          <h3 className="text-base font-medium text-emerald-700 dark:text-emerald-300 mb-1.5 flex items-center gap-1.5">
            <Activity className="w-4 h-4"/>
            Суточная норма (TDEE)
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" aria-live="polite">
              {formatNumber(results.tdee, 0)} ккал
            </span>
          </div>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            Общий расход энергии с учетом вашей активности
          </p>
        </div>

        {/* Target Calories */}
        <div className="rounded-lg bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 p-5 border border-purple-100 dark:border-purple-800/50 shadow-sm">
          <h3 className="text-base font-medium text-purple-700 dark:text-purple-300 mb-1.5 flex items-center gap-1.5">
            <Target className="w-4 h-4"/>
            Целевые калории
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-purple-800 dark:text-purple-200" aria-live="polite">
              {formatNumber(results.targetCalories, 0)} ккал
            </span>
          </div>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            {getGoalName(results.goal)}
            {results.weeklyChange > 0 && results.goal !== 'maintain' ? 
              ` (${formatNumber(results.weeklyChange, 2)} кг/нед)` : ''}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Использованная формула" 
          value={getFormulaName(results.formula)} 
          color="blue" 
          icon={<Calculator className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Уровень активности" 
          value={getActivityName(results.activityLevel)} 
          color="emerald" 
          icon={<Dumbbell className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Параметры" 
          value={`${results.gender === 'male' ? 'Мужчина' : 'Женщина'}, ${results.age} лет, ${results.weight.value} ${results.weight.unit}, ${results.height.value} ${results.height.unit}`} 
          icon={<Calendar className="w-4 h-4"/>} 
        />
        
        {results.bodyFat && (
          <ResultItem 
            label="Процент жира в организме" 
            value={`${results.bodyFat}%`} 
            color="orange" 
            icon={<Percent className="w-4 h-4"/>} 
          />
        )}

        {results.goal !== 'maintain' && (
          <ResultItem 
            label={results.goal.includes('lose') ? "Рекомендуемый дефицит калорий" : "Рекомендуемый профицит калорий"} 
            value={`${formatNumber(Math.abs(results.tdee - results.targetCalories), 0)} ккал в день (${formatNumber(Math.abs(100 - results.goalFactor * 100), 0)}%)`} 
            color={results.goal.includes('lose') ? "amber" : "emerald"} 
            icon={<ArrowRight className="w-4 h-4"/>} 
          />
        )}
      </div>

      {/* Информационный блок */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <BarChart2 className="w-5 h-5 text-emerald-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Что такое TDEE?</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              TDEE (Total Daily Energy Expenditure) — общий суточный расход энергии. Это количество калорий, которое ваш организм расходует в течение дня, включая базовый метаболизм, физическую активность, терморегуляцию и пищеварение. 
              При потреблении калорий равном вашему TDEE, вес будет стабильным.
            </p>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;